/**
 * Finance import wizard (FIN-10) — the weekly <10-minute loop lives here.
 *
 * One screen, four inline steps:
 *   1. File + account     — pick the account, drop the CSV; parsed client-side.
 *   2. Mapping            — map columns → date / description / amount (or a
 *                           debit + credit pair) / optional bank fields /
 *                           amountEur for non-EUR accounts. Prefilled from the
 *                           newest batch's columnMapping and collapsed to a
 *                           one-liner when the prefill still fits the file.
 *   3. Preview            — POST /imports/preview; duplicates pre-unchecked and
 *                           badged; pt-PT vs en number-format toggle; totals row.
 *   4. Commit             — POST /imports with only the kept rows; success panel
 *                           with the four counts + "Review now" / "Import another".
 *
 * Goal: minimize clicks. Remembered mapping + pre-unchecked duplicates mean a
 * repeat import of the same bank is upload → confirm.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useFinanceAccounts, useFinanceImportBatches } from '../../api/finance';
import {
  usePreviewImport,
  useCommitImport,
  type ImportRowInput,
  type ImportPreviewRow,
} from '../../api/finance';
import {
  parseCsv,
  parseAmount,
  parseDate,
  detectDateFormat,
  detectNumberFormat,
  DATE_FORMATS,
  type NumberFormat,
  type DateFormat,
} from '../../lib/csv';
import { fetchEurRates, type EurRateLookup } from '../../lib/fx';
import { formatEur, financeErrorMessage } from './finance-format';
import styles from './finance.module.css';

// The logical fields the user maps CSV columns onto. `none` = not mapped.
type Field =
  | 'date'
  | 'description'
  | 'amount'
  | 'debit'
  | 'credit'
  | 'amountEur'
  | 'bankCategory'
  | 'bankType'
  | 'balanceAfter';

type AmountMode = 'single' | 'split';

interface Mapping {
  amountMode: AmountMode;
  dateFormat: DateFormat;
  numberFormat: NumberFormat;
  /** Non-EUR only: derive amountEur by fetching daily ECB rates instead of
   *  mapping an EUR column. */
  autoFx: boolean;
  /** column index (string from the <select>) per field; '' = unmapped */
  columns: Record<Field, string>;
}

const NONE = '';

const emptyMapping = (): Mapping => ({
  amountMode: 'single',
  dateFormat: 'DD-MM-YYYY',
  numberFormat: 'pt',
  autoFx: false,
  columns: {
    date: NONE,
    description: NONE,
    amount: NONE,
    debit: NONE,
    credit: NONE,
    amountEur: NONE,
    bankCategory: NONE,
    bankType: NONE,
    balanceAfter: NONE,
  },
});

interface NormalizedRow {
  rowIndex: number;
  ok: boolean;
  error?: string;
  input?: ImportRowInput;
}

export function FinanceImportScreen() {
  const accountsQ = useFinanceAccounts();
  const accounts = useMemo(
    () => (accountsQ.data ?? []).filter((a) => !a.archivedAt),
    [accountsQ.data],
  );

  const [accountId, setAccountId] = useState('');
  const [filename, setFilename] = useState('');
  const [parsed, setParsed] = useState<{ headers: string[]; rows: string[][] } | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const account = accounts.find((a) => a.id === accountId) ?? null;
  const isEur = account ? account.currency === 'EUR' : true;

  const batchesQ = useFinanceImportBatches(accountId || undefined);

  const [mapping, setMapping] = useState<Mapping>(emptyMapping);
  const [mappingTouched, setMappingTouched] = useState(false);
  const [mappingExpanded, setMappingExpanded] = useState(false);

  // Auto-FX state (non-EUR accounts converting via daily ECB rates).
  const [fxRates, setFxRates] = useState<EurRateLookup | null>(null);
  const [fxLoading, setFxLoading] = useState(false);
  const [fxError, setFxError] = useState<string | null>(null);
  // Ref to the most-recently parsed headers so the prefill effect can access them
  // without being listed as a dependency (headers don't change after file parse).
  const parsedHeadersRef = useRef<string[] | null>(null);

  // Preview + commit state.
  const preview = usePreviewImport();
  const commit = useCommitImport();
  const [previewRows, setPreviewRows] = useState<ImportPreviewRow[] | null>(null);
  const [kept, setKept] = useState<Set<number>>(new Set());
  const [committed, setCommitted] = useState<null | {
    inserted: number;
    autoCategorized: number;
    transfersConfirmed: number;
    transfersSuggested: number;
  }>(null);

  // ─── File handling ──────────────────────────────────────────────────────
  const onFile = async (file: File | null) => {
    if (!file) return;
    setParseError(null);
    setPreviewRows(null);
    setCommitted(null);
    setFxRates(null);
    setFxError(null);
    setFilename(file.name);
    try {
      const text = await file.text();
      const result = parseCsv(text);
      if (result.headers.length === 0) {
        setParseError('That file had no readable rows.');
        setParsed(null);
        parsedHeadersRef.current = null;
        return;
      }
      setParsed(result);
      parsedHeadersRef.current = result.headers;
      if (batchesQ.isLoading) {
        // Batches haven't loaded yet — apply empty mapping for now and let the
        // effect below re-run the prefill once batchesQ.data arrives.
        setMapping(emptyMapping());
        setMappingTouched(false);
        setMappingExpanded(true);
      } else {
        // Batches already available — prefill immediately.
        const prefill = prefillFromBatch(result.headers, isEur);
        setMapping(prefill.mapping);
        setMappingTouched(false);
        setMappingExpanded(!prefill.applied);
      }
    } catch {
      setParseError('Could not read that file.');
      setParsed(null);
      parsedHeadersRef.current = null;
    }
  };

  // Re-run prefill when batch data arrives after the file was already parsed
  // (covers first-ever import and slow-network scenarios). Only applies when
  // the user hasn't manually touched the mapping yet.
  useEffect(() => {
    if (!batchesQ.data || mappingTouched || !parsedHeadersRef.current) return;
    const headers = parsedHeadersRef.current;
    const prefill = prefillFromBatch(headers, isEur);
    setMapping(prefill.mapping);
    setMappingExpanded(!prefill.applied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchesQ.data]);

  // Fetch ECB daily rates for the file's date span when auto-convert is enabled
  // on a non-EUR account. Re-runs when the currency, date column, or date format
  // changes; the resulting lookup is consumed in `normalizeRow`.
  useEffect(() => {
    if (isEur || !mapping.autoFx || !parsed || !account || mapping.columns.date === NONE) {
      return;
    }
    const isoDates = parsed.rows
      .map((cells) => parseDate(cellAt(cells, mapping.columns.date), mapping.dateFormat))
      .filter((d): d is string => !!d)
      .sort();
    if (isoDates.length === 0) {
      setFxRates(null);
      return;
    }
    const start = isoDates[0]!;
    const end = isoDates[isoDates.length - 1]!;
    let cancelled = false;
    setFxLoading(true);
    setFxError(null);
    fetchEurRates(account.currency, start, end)
      .then((lookup) => {
        if (cancelled) return;
        setFxRates(lookup);
        setFxLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFxRates(null);
        setFxError(err instanceof Error ? err.message : 'Exchange-rate lookup failed.');
        setFxLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isEur, mapping.autoFx, mapping.columns.date, mapping.dateFormat, parsed, account]);

  // Prefill mapping from the newest stored columnMapping when its column names
  // still exist in the just-parsed header row.
  function prefillFromBatch(
    headers: string[],
    eur: boolean,
  ): { mapping: Mapping; applied: boolean } {
    const stored = batchesQ.data?.[0]?.columnMapping as StoredMapping | undefined;
    if (!stored || typeof stored !== 'object') return { mapping: emptyMapping(), applied: false };

    const idxOf = (name: unknown): string => {
      if (typeof name !== 'string') return NONE;
      const i = headers.indexOf(name);
      return i >= 0 ? String(i) : NONE;
    };

    const m = emptyMapping();
    m.amountMode = stored.amountMode === 'split' ? 'split' : 'single';
    if (stored.dateFormat && DATE_FORMATS.includes(stored.dateFormat as DateFormat)) {
      m.dateFormat = stored.dateFormat as DateFormat;
    }
    if (stored.numberFormat === 'en' || stored.numberFormat === 'pt') {
      m.numberFormat = stored.numberFormat;
    }
    m.autoFx = stored.autoFx === true;
    m.columns.date = idxOf(stored.columns?.date);
    m.columns.description = idxOf(stored.columns?.description);
    m.columns.amount = idxOf(stored.columns?.amount);
    m.columns.debit = idxOf(stored.columns?.debit);
    m.columns.credit = idxOf(stored.columns?.credit);
    m.columns.amountEur = idxOf(stored.columns?.amountEur);
    m.columns.bankCategory = idxOf(stored.columns?.bankCategory);
    m.columns.bankType = idxOf(stored.columns?.bankType);
    m.columns.balanceAfter = idxOf(stored.columns?.balanceAfter);

    const applied = mappingComplete(m, eur);
    return { mapping: m, applied };
  }

  const setColumn = (field: Field, value: string) => {
    setMappingTouched(true);
    setMapping((prev) => {
      const next: Mapping = { ...prev, columns: { ...prev.columns, [field]: value } };
      // Auto-detect the format from the chosen column's sample values, so the
      // user doesn't have to know whether it's YYYY-MM-DD vs DD/MM/YYYY or
      // pt vs en numbers. Re-runs whenever the mapped column changes.
      if (value !== NONE && parsed) {
        const idx = Number(value);
        const samples = parsed.rows
          .slice(0, 200)
          .map((r) => r[idx] ?? '')
          .filter((v) => v.trim() !== '');
        if (field === 'date') {
          const fmt = detectDateFormat(samples);
          if (fmt) next.dateFormat = fmt;
        } else if (field === 'amount' || field === 'debit' || field === 'credit') {
          next.numberFormat = detectNumberFormat(samples);
        }
      }
      return next;
    });
  };

  const complete = mappingComplete(mapping, isEur);
  const prefilled = !mappingTouched && complete && !!batchesQ.data?.[0];

  // ─── Normalize parsed rows under the current mapping ─────────────────────
  const normalized = useMemo<NormalizedRow[]>(() => {
    if (!parsed || !complete) return [];
    return parsed.rows.map((cells, rowIndex) =>
      normalizeRow(cells, mapping, rowIndex, isEur, fxRates),
    );
  }, [parsed, mapping, complete, isEur, fxRates]);

  const validRows = normalized.filter((r) => r.ok && r.input);
  const invalidCount = normalized.length - validRows.length;

  // ─── Preview ─────────────────────────────────────────────────────────────
  const runPreview = () => {
    if (!accountId || validRows.length === 0) return;
    preview.mutate(
      { accountId, rows: validRows.map((r) => r.input!) },
      {
        onSuccess: (res) => {
          setPreviewRows(res.rows);
          // Pre-uncheck duplicates: keep every non-duplicate row.
          const next = new Set<number>();
          res.rows.forEach((row, i) => {
            if (!row.duplicate) next.add(i);
          });
          setKept(next);
          setCommitted(null);
        },
      },
    );
  };

  const toggleKept = (i: number) =>
    setKept((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  // ─── Commit ──────────────────────────────────────────────────────────────
  const runCommit = () => {
    if (!previewRows || !accountId) return;
    const rows: ImportRowInput[] = previewRows
      .filter((_, i) => kept.has(i))
      .map((r) => ({
        date: r.date,
        description: r.description,
        amount: r.amount,
        ...(r.amountEur !== undefined ? { amountEur: r.amountEur } : {}),
        ...(r.bankCategory ? { bankCategory: r.bankCategory } : {}),
        ...(r.bankType ? { bankType: r.bankType } : {}),
        ...(r.balanceAfter !== undefined ? { balanceAfter: r.balanceAfter } : {}),
      }));
    if (rows.length === 0) return;

    commit.mutate(
      { accountId, filename, columnMapping: serializeMapping(mapping, parsed!.headers), rows },
      {
        onSuccess: (res) => {
          setCommitted({
            inserted: res.inserted,
            autoCategorized: res.autoCategorized,
            transfersConfirmed: res.transfersConfirmed,
            transfersSuggested: res.transfersSuggested,
          });
          setPreviewRows(null);
        },
      },
    );
  };

  const resetForAnother = () => {
    setParsed(null);
    setFilename('');
    setPreviewRows(null);
    setCommitted(null);
    setMapping(emptyMapping());
    setMappingTouched(false);
    setParseError(null);
    setFxRates(null);
    setFxError(null);
  };

  const keptCount = previewRows ? previewRows.filter((_, i) => kept.has(i)).length : 0;
  const keptSum = previewRows
    ? previewRows.reduce((s, r, i) => (kept.has(i) ? s + (r.amountEur ?? r.amount) : s), 0)
    : 0;

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h2 className={styles.title}>Import</h2>
          <div className={styles.subtitle}>
            Upload a bank CSV, confirm the mapping (remembered per account), skim
            the duplicate flags, and commit. Rules and transfer matching run
            automatically on commit.
          </div>
        </div>
      </header>

      {committed ? (
        <SuccessPanel result={committed} onAnother={resetForAnother} />
      ) : (
        <div className={styles.wizard}>
          {/* Step 1 — file + account */}
          <Step n={1} title="File and account">
            <div className={styles.stepRow}>
              <div className={styles.stepField}>
                <label className={styles.fieldLabel} htmlFor="imp-account">
                  Account
                </label>
                <select
                  id="imp-account"
                  className={styles.select}
                  value={accountId}
                  onChange={(e) => {
                    setAccountId(e.target.value);
                    setPreviewRows(null);
                    setCommitted(null);
                  }}
                >
                  <option value="">— choose an account —</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.currency})
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.stepField}>
                <label className={styles.fieldLabel} htmlFor="imp-file">
                  CSV file
                </label>
                <input
                  id="imp-file"
                  className={styles.input}
                  type="file"
                  accept=".csv,text/csv"
                  disabled={!accountId}
                  onChange={(e) => onFile(e.target.files?.[0] ?? null)}
                />
                {!accountId && (
                  <span className={styles.fieldHint}>Choose an account first.</span>
                )}
                {filename && <span className={styles.fieldHint}>Loaded: {filename}</span>}
              </div>
            </div>
            {parseError && <div className={styles.error}>{parseError}</div>}
            {parsed && (
              <div className={styles.fieldHint}>
                {parsed.headers.length} columns · {parsed.rows.length} data rows detected.
              </div>
            )}
          </Step>

          {/* Step 2 — mapping */}
          {parsed && (
            <Step n={2} title="Column mapping">
              {prefilled && !mappingExpanded ? (
                <div className={styles.mapSummary}>
                  <span>
                    Mapping reused from your last import for this account
                    {' '}({mapping.amountMode === 'split' ? 'debit/credit columns' : 'single amount column'},
                    {' '}dates {mapping.dateFormat}).
                  </span>
                  <button
                    type="button"
                    className={styles.linkBtn}
                    onClick={() => setMappingExpanded(true)}
                  >
                    edit mapping
                  </button>
                </div>
              ) : (
                <MappingEditor
                  headers={parsed.headers}
                  mapping={mapping}
                  isEur={isEur}
                  currency={account?.currency ?? ''}
                  fxLoading={fxLoading}
                  fxError={fxError}
                  onSetColumn={setColumn}
                  onSetMapping={(patch) => {
                    setMappingTouched(true);
                    setMapping((prev) => ({ ...prev, ...patch }));
                  }}
                />
              )}

              {complete ? (
                <div className={styles.stepActions}>
                  <span className={styles.fieldHint}>
                    {!isEur && mapping.autoFx && fxLoading
                      ? 'Converting to EUR via daily rates…'
                      : `${validRows.length} rows ready${
                          invalidCount > 0 ? ` · ${invalidCount} skipped (unparseable)` : ''
                        }`}
                  </span>
                  <button
                    type="button"
                    className="kos-btn kos-btn-primary"
                    disabled={validRows.length === 0 || preview.isPending || fxLoading}
                    onClick={runPreview}
                  >
                    {preview.isPending ? 'Previewing…' : 'Preview import'}
                  </button>
                </div>
              ) : (
                <div className={styles.fieldHint}>
                  Map at least a date, a description, and an amount
                  {mapping.amountMode === 'split' ? ' (debit and credit)' : ''}
                  {!isEur ? ', plus the EUR amount (or tick auto-convert)' : ''} to continue.
                </div>
              )}
              {preview.isError && (
                <div className={styles.error}>{financeErrorMessage(preview.error)}</div>
              )}
              {invalidCount > 0 && complete && (
                <details className={styles.skippedDetails}>
                  <summary>{invalidCount} rows can&apos;t be parsed — show why</summary>
                  <ul>
                    {normalized
                      .filter((r) => !r.ok)
                      .slice(0, 20)
                      .map((r) => (
                        <li key={r.rowIndex}>
                          Row {r.rowIndex + 2}: {r.error}
                        </li>
                      ))}
                  </ul>
                </details>
              )}
            </Step>
          )}

          {/* Step 3 — preview */}
          {previewRows && (
            <Step n={3} title="Preview and dedup">
              <div className={styles.stepActions}>
                <span className={styles.fieldHint}>
                  Duplicates are pre-unchecked. Tick one to import it anyway.
                </span>
              </div>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ width: 36 }} aria-label="Keep" />
                      <th>Date</th>
                      <th>Description</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, i) => (
                      <tr key={i} className={kept.has(i) ? undefined : styles.archived}>
                        <td>
                          <input
                            type="checkbox"
                            checked={kept.has(i)}
                            onChange={() => toggleKept(i)}
                          />
                        </td>
                        <td className={styles.num} style={{ textAlign: 'left' }}>
                          {row.date}
                        </td>
                        <td>{row.description}</td>
                        <td
                          className={`${styles.num} ${
                            row.amount < 0 ? styles.amtNeg : styles.amtPos
                          }`}
                        >
                          {formatEur(row.amountEur ?? row.amount)}
                        </td>
                        <td>
                          {row.duplicate && <span className={styles.badge}>duplicate</span>}
                          {row.needsEur && (
                            <span className={`${styles.badge} ${styles.badgeExpense}`}>
                              needs EUR
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td />
                      <td colSpan={2} className={styles.totalsLabel}>
                        Keeping {keptCount} of {previewRows.length} rows
                      </td>
                      <td className={styles.num}>{formatEur(keptSum)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className={styles.stepActions}>
                <button
                  type="button"
                  className="kos-btn kos-btn-primary"
                  disabled={keptCount === 0 || commit.isPending}
                  onClick={runCommit}
                >
                  {commit.isPending ? 'Importing…' : `Import ${keptCount} rows`}
                </button>
              </div>
              {commit.isError && (
                <div className={styles.error}>{financeErrorMessage(commit.error)}</div>
              )}
            </Step>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <section className={styles.step}>
      <div className={styles.stepHead}>
        <span className={styles.stepNum}>{n}</span>
        <span className={styles.stepTitle}>{title}</span>
      </div>
      <div className={styles.stepBody}>{children}</div>
    </section>
  );
}

function MappingEditor({
  headers,
  mapping,
  isEur,
  currency,
  fxLoading,
  fxError,
  onSetColumn,
  onSetMapping,
}: {
  headers: string[];
  mapping: Mapping;
  isEur: boolean;
  currency: string;
  fxLoading: boolean;
  fxError: string | null;
  onSetColumn: (field: Field, value: string) => void;
  onSetMapping: (patch: Partial<Mapping>) => void;
}) {
  const colSelect = (field: Field, label: string, hint?: string) => (
    <div className={styles.stepField}>
      <label className={styles.fieldLabel}>{label}</label>
      <select
        aria-label={label}
        className={styles.select}
        value={mapping.columns[field]}
        onChange={(e) => onSetColumn(field, e.target.value)}
      >
        <option value={NONE}>— none —</option>
        {headers.map((h, i) => (
          <option key={i} value={String(i)}>
            {h || `(column ${i + 1})`}
          </option>
        ))}
      </select>
      {hint && <span className={styles.fieldHint}>{hint}</span>}
    </div>
  );

  return (
    <div className={styles.mapGrid}>
      <div className={styles.stepRow}>
        {colSelect('date', 'Date column')}
        <div className={styles.stepField}>
          <label className={styles.fieldLabel}>Date format</label>
          <select
            aria-label="Date format"
            className={styles.select}
            value={mapping.dateFormat}
            onChange={(e) => onSetMapping({ dateFormat: e.target.value as DateFormat })}
          >
            {DATE_FORMATS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </div>
      </div>

      {colSelect('description', 'Description column')}

      <div className={styles.stepRow}>
        <div className={styles.stepField}>
          <label className={styles.fieldLabel}>Amount columns</label>
          <select
            aria-label="Amount columns"
            className={styles.select}
            value={mapping.amountMode}
            onChange={(e) => onSetMapping({ amountMode: e.target.value as AmountMode })}
          >
            <option value="single">Single signed amount</option>
            <option value="split">Separate debit / credit</option>
          </select>
        </div>
        <div className={styles.stepField}>
          <label className={styles.fieldLabel}>Number format</label>
          <select
            aria-label="Number format"
            className={styles.select}
            value={mapping.numberFormat}
            onChange={(e) => onSetMapping({ numberFormat: e.target.value as NumberFormat })}
          >
            <option value="pt">1.234,56 (pt-PT)</option>
            <option value="en">1,234.56 (en)</option>
          </select>
        </div>
      </div>

      {mapping.amountMode === 'single' ? (
        colSelect('amount', 'Amount column', 'Negative = expense, positive = income.')
      ) : (
        <div className={styles.stepRow}>
          {colSelect('debit', 'Debit column', 'Money out → stored as negative.')}
          {colSelect('credit', 'Credit column', 'Money in → stored as positive.')}
        </div>
      )}

      {!isEur && (
        <div className={styles.stepField}>
          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={mapping.autoFx}
              onChange={(e) => onSetMapping({ autoFx: e.target.checked })}
            />
            Convert to EUR automatically using daily ECB rates
          </label>
          {mapping.autoFx ? (
            fxLoading ? (
              <span className={styles.fieldHint}>Fetching exchange rates…</span>
            ) : fxError ? (
              <span className={styles.error}>{fxError}</span>
            ) : (
              <span className={styles.fieldHint}>
                Daily ECB {currency || 'foreign-currency'}→EUR reference rates (via
                Frankfurter); weekends/holidays use the prior business day.
              </span>
            )
          ) : (
            colSelect(
              'amountEur',
              'EUR amount column',
              'Required for non-EUR accounts — or tick auto-convert above.',
            )
          )}
        </div>
      )}

      <div className={styles.stepRow}>
        {colSelect('bankCategory', 'Bank category (optional)')}
        {colSelect('bankType', 'Bank type (optional)')}
      </div>
      {colSelect('balanceAfter', 'Balance after (optional)')}
    </div>
  );
}

function SuccessPanel({
  result,
  onAnother,
}: {
  result: {
    inserted: number;
    autoCategorized: number;
    transfersConfirmed: number;
    transfersSuggested: number;
  };
  onAnother: () => void;
}) {
  return (
    <div className={styles.successPanel}>
      <h3 className={styles.successTitle}>Import complete</h3>
      <div className={styles.successStats}>
        <Stat label="Inserted" value={result.inserted} />
        <Stat label="Auto-categorized" value={result.autoCategorized} />
        <Stat label="Transfers confirmed" value={result.transfersConfirmed} />
        <Stat label="Transfers suggested" value={result.transfersSuggested} />
      </div>
      <div className={styles.stepActions}>
        <Link to="/finance/review" className="kos-btn kos-btn-primary">
          Review now
        </Link>
        <button type="button" className="kos-btn" onClick={onAnother}>
          Import another file
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className={styles.statCard}>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );
}

// ─── Mapping logic ──────────────────────────────────────────────────────────

interface StoredMapping {
  amountMode?: string;
  dateFormat?: string;
  numberFormat?: string;
  autoFx?: boolean;
  columns?: Partial<Record<Field, string>>;
}

function mappingComplete(m: Mapping, isEur: boolean): boolean {
  if (m.columns.date === NONE || m.columns.description === NONE) return false;
  if (m.amountMode === 'single') {
    if (m.columns.amount === NONE) return false;
  } else {
    if (m.columns.debit === NONE && m.columns.credit === NONE) return false;
  }
  // Non-EUR: either map an explicit EUR column or auto-convert via daily rates.
  if (!isEur && m.columns.amountEur === NONE && !m.autoFx) return false;
  return true;
}

/** Store the mapping by COLUMN NAME so it survives reordered exports. */
function serializeMapping(m: Mapping, headers: string[]): Record<string, unknown> {
  const nameAt = (idx: string): string | null =>
    idx === NONE ? null : (headers[Number(idx)] ?? null);
  return {
    amountMode: m.amountMode,
    dateFormat: m.dateFormat,
    numberFormat: m.numberFormat,
    autoFx: m.autoFx,
    columns: {
      date: nameAt(m.columns.date),
      description: nameAt(m.columns.description),
      amount: nameAt(m.columns.amount),
      debit: nameAt(m.columns.debit),
      credit: nameAt(m.columns.credit),
      amountEur: nameAt(m.columns.amountEur),
      bankCategory: nameAt(m.columns.bankCategory),
      bankType: nameAt(m.columns.bankType),
      balanceAfter: nameAt(m.columns.balanceAfter),
    },
  };
}

function cellAt(cells: string[], idx: string): string {
  if (idx === NONE) return '';
  return cells[Number(idx)] ?? '';
}

function normalizeRow(
  cells: string[],
  m: Mapping,
  rowIndex: number,
  isEur: boolean,
  fx: EurRateLookup | null,
): NormalizedRow {
  const fail = (error: string): NormalizedRow => ({ rowIndex, ok: false, error });

  const isoDate = parseDate(cellAt(cells, m.columns.date), m.dateFormat);
  if (!isoDate) return fail('unreadable date');

  const description = cellAt(cells, m.columns.description).trim();
  if (!description) return fail('empty description');

  // Amount: single signed column, or debit/credit pair combined.
  let amount: number | null;
  if (m.amountMode === 'single') {
    amount = parseAmount(cellAt(cells, m.columns.amount), m.numberFormat);
  } else {
    const debit = parseAmount(cellAt(cells, m.columns.debit), m.numberFormat);
    const credit = parseAmount(cellAt(cells, m.columns.credit), m.numberFormat);
    // Debit = money out (negative), credit = money in (positive). One side is
    // usually blank/0; magnitudes are taken absolute then signed.
    const d = debit ? Math.abs(debit) : 0;
    const cr = credit ? Math.abs(credit) : 0;
    if (d === 0 && cr === 0) amount = null;
    else amount = cr - d;
  }
  if (amount === null) return fail('unreadable amount');
  if (amount === 0) return fail('zero amount');

  const input: ImportRowInput = { date: isoDate, description, amount };

  if (!isEur) {
    if (m.autoFx) {
      const rate = fx?.rateFor(isoDate) ?? null;
      if (rate === null) return fail(`no exchange rate for ${isoDate}`);
      input.amountEur = Math.round(amount * rate * 100) / 100;
    } else {
      const eur = parseAmount(cellAt(cells, m.columns.amountEur), m.numberFormat);
      if (eur === null) return fail('unreadable EUR amount');
      input.amountEur = eur;
    }
  }

  const bankCategory = cellAt(cells, m.columns.bankCategory).trim();
  if (bankCategory) input.bankCategory = bankCategory;
  const bankType = cellAt(cells, m.columns.bankType).trim();
  if (bankType) input.bankType = bankType;
  const balanceAfter = parseAmount(cellAt(cells, m.columns.balanceAfter), m.numberFormat);
  if (balanceAfter !== null) input.balanceAfter = balanceAfter;

  return { rowIndex, ok: true, input };
}
